import { B, World, valueNoise, mulberry32, PEDESTAL_COUNT, type MapChest, type MapData, type Vec3i } from "./world";

// Valleyside, after the 2013 MCGamer map: a college town in a bowl ringed by
// mountains. The ground climbs steadily from the central park (spawn and
// deathmatch arena) to the mountain foot, then walls up to the map edge.
// The seed shuffles which landmark sits where, how they're turned, the river's
// course and which mountain gets the cabin; the set of landmarks never changes.
//
// It's built in two passes: first a heightmap that every feature carves
// (building pads, the stair path, the river), then voxels and the buildings.
// Nothing here may touch world.ts exports at module load (world.ts imports us).

const S = 160;
const SY = 56;
const C = S / 2;
const PARK_Y = 12;
const ARENA_R = 16;
const RING_IN = 17.5;
const RING_OUT = 20.5;
/** Where the stair path starts and the base station stands: just short of the mountain foot. */
const LIFT_BACK = 5;

// What a column has been claimed for, so later features keep out of the way.
const F_PARK = 1, F_ROAD = 2, F_BUILD = 4, F_ZONE = 8, F_KEEP = 16;

const idx = (x: number, z: number) => z * S + x;
const inMap = (x: number, z: number) => x >= 0 && z >= 0 && x < S && z < S;
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

/** A building's footprint. Local x runs along the front, local z from the front (0, facing the park) to the back. */
class Plot {
  /** World-space size of the footprint. */
  readonly wx: number;
  readonly wz: number;
  constructor(
    public w: World, public x0: number, public z0: number,
    public W: number, public D: number, public rot: number, public y = 0,
  ) {
    this.wx = rot % 2 ? D : W;
    this.wz = rot % 2 ? W : D;
  }
  /** rot 0: front faces -z, 1: +x, 2: +z, 3: -x. */
  pos(lx: number, lz: number): [number, number] {
    const { x0, z0, W, D } = this;
    switch (this.rot) {
      case 0: return [x0 + lx, z0 + lz];
      case 1: return [x0 + (D - 1 - lz), z0 + lx];
      case 2: return [x0 + (W - 1 - lx), z0 + (D - 1 - lz)];
      default: return [x0 + lz, z0 + (W - 1 - lx)];
    }
  }
  /** ly is relative to the pad: 0 is the floor, 1 the first layer you stand in. */
  set(lx: number, ly: number, lz: number, b: number) {
    const [x, z] = this.pos(lx, lz);
    this.w.set(x, this.y + ly, z, b);
  }
  fill(lx0: number, ly0: number, lz0: number, lx1: number, ly1: number, lz1: number, b: number) {
    for (let ly = ly0; ly <= ly1; ly++) for (let lz = lz0; lz <= lz1; lz++) for (let lx = lx0; lx <= lx1; lx++) this.set(lx, ly, lz, b);
  }
  chest(out: MapChest[], lx: number, ly: number, lz: number, good = false) {
    const [x, z] = this.pos(lx, lz);
    this.w.set(x, this.y + ly, z, B.AIR);
    out.push({ x, y: this.y + ly, z, good });
  }
  /** Just outside the middle of the front wall, where a road should arrive. */
  door(): [number, number] {
    const [x, z] = this.pos((this.W - 1) / 2, -1);
    return [x, z];
  }
  center(): [number, number] {
    return [this.x0 + this.wx / 2, this.z0 + this.wz / 2];
  }
}

/** Turn a footprint so its front faces the park. */
function facing(bx: number, bz: number): number {
  const dx = C - bx, dz = C - bz;
  if (Math.abs(dx) > Math.abs(dz)) return dx > 0 ? 1 : 3;
  return dz > 0 ? 2 : 0;
}

/** How far you can go from the centre toward angle a before the map edge (minus a margin). */
function edgeR(a: number, margin: number) {
  return (C - margin) / Math.max(Math.abs(Math.cos(a)), Math.abs(Math.sin(a)));
}

export function generateValley(seed: number): MapData {
  const w = new World(S, SY, S);
  const rand = mulberry32(seed * 31 + 7);
  const chests: MapChest[] = [];
  const h = new Int16Array(S * S);
  const water = new Int16Array(S * S); // water surface per column, 0 = dry
  const top = new Uint8Array(S * S); // forced surface block, 0 = natural
  const flags = new Uint8Array(S * S);
  const landmarks: Record<string, Vec3i> = {};
  const mark = (name: string, x: number, y: number, z: number) => (landmarks[name] = { x: Math.round(x), y, z: Math.round(z) });

  // ---------- heightmap: park, rising campus, mountain wall ----------

  /** Mountain foot radius by angle: the bowl isn't perfectly round. */
  const foot = (a: number) => 61 + (valueNoise(Math.cos(a) * 18 + 500, Math.sin(a) * 18 + 500, 6, seed + 11) - 0.5) * 10;
  const campus = (x: number, z: number, r: number) =>
    PARK_Y + Math.max(0, r - RING_OUT - 1) * 0.16 + (valueNoise(x, z, 13, seed + 3) - 0.5) * 3 * clamp((r - 24) / 8, 0, 1);

  for (let z = 0; z < S; z++) for (let x = 0; x < S; x++) {
    const dx = x - C, dz = z - C;
    const r = Math.hypot(dx, dz);
    let y = campus(x, z, r);
    const m = r - foot(Math.atan2(dz, dx));
    if (m > 0) {
      // Steepness wanders, so some faces are a walk up and others a cliff.
      const steep = 0.8 + valueNoise(x, z, 22, seed + 5) * 1.3;
      y += m * steep + valueNoise(x, z, 9, seed + 7) * Math.min(1, m / 6) * 9;
      y = Math.min(y, SY - 6 - valueNoise(x, z, 7, seed + 9) * 8);
    }
    h[idx(x, z)] = Math.round(y);
  }
  const H = (x: number, z: number) => h[idx(clamp(Math.round(x), 0, S - 1), clamp(Math.round(z), 0, S - 1))]!;

  for (let z = 0; z < S; z++) for (let x = 0; x < S; x++) {
    if (Math.hypot(x - C, z - C) <= RING_OUT + 1) {
      h[idx(x, z)] = PARK_Y;
      flags[idx(x, z)]! |= F_PARK;
    }
  }

  // ---------- layout: six sectors around the park, shuffled per seed ----------

  const kinds = ["stadium", "library", "dorms", "garage", "river", "lift"];
  for (let i = kinds.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [kinds[i], kinds[j]] = [kinds[j]!, kinds[i]!];
  }
  const phase = rand() * Math.PI * 2;
  const sectorAngle = (k: string) => phase + (kinds.indexOf(k) / kinds.length) * Math.PI * 2 + (rand() - 0.5) * 0.25;

  /** Stamp a disc of flags (and optionally note each cell). */
  const disc = (px: number, pz: number, rad: number, f: number, each?: (x: number, z: number, d: number) => void) => {
    for (let z = Math.floor(pz - rad); z <= Math.ceil(pz + rad); z++) for (let x = Math.floor(px - rad); x <= Math.ceil(px + rad); x++) {
      if (!inMap(x, z)) continue;
      const d = Math.hypot(x - px, z - pz);
      if (d > rad) continue;
      flags[idx(x, z)]! |= f;
      each?.(x, z, d);
    }
  };

  // River: a waterfall off the mountain, a gorge, then a meander down to a pond.
  const ra = sectorAngle("river");
  const river = (() => {
    const u = [Math.cos(ra), Math.sin(ra)] as const, v = [-u[1], u[0]] as const;
    const f = foot(ra);
    const rTop = Math.min(f + 14, edgeR(ra, 4));
    const rCliff = rTop - 6;
    const rPool = f - 2;
    const rEnd = 31;
    const amp = 4 + rand() * 3, wave = (Math.PI * 2) / (18 + rand() * 10), ph = rand() * Math.PI * 2;
    const pts: { x: number; z: number; r: number; rad: number }[] = [];
    for (let r = rTop; r >= rEnd; r -= 0.5) {
      const lat = r < rPool ? amp * Math.sin((rPool - r) * wave + ph) * Math.min(1, (rPool - r) / 6) : 0;
      const rad = r >= rCliff ? 1.3 : r >= rPool - 1 ? 2.6 : 1.7;
      pts.push({ x: C + u[0] * r + v[0] * lat, z: C + u[1] * r + v[1] * lat, r, rad });
    }
    for (const p of pts) disc(p.x, p.z, p.rad + 3, F_ZONE);
    const end = pts.at(-1)!;
    disc(end.x, end.z, 9, F_ZONE);
    return { pts, rCliff, end };
  })();

  // Chairlift: a stair path straight up the mountain to a cabin, posts alongside.
  const la = sectorAngle("lift");
  const lift = (() => {
    const u = [Math.cos(la), Math.sin(la)] as const, v = [-u[1], u[0]] as const;
    const f = foot(la);
    const rA = f - LIFT_BACK;
    const rB = Math.min(f + 11, edgeR(la, 4) - 9);
    const ax = C + u[0] * rA, az = C + u[1] * rA;
    const len = rB - rA;
    const cabR = rB + 4.5;
    const cx = Math.round(C + u[0] * cabR), cz = Math.round(C + u[1] * cabR);
    for (let t = -2; t <= len + 1; t += 0.5) {
      for (let s = -2.5; s <= 3.5; s += 0.5) disc(ax + u[0] * t + v[0] * s, az + u[1] * t + v[1] * s, 0.8, F_ZONE);
    }
    for (let z = cz - 4; z < cz + 5; z++) for (let x = cx - 4; x < cx + 5; x++) flags[idx(x, z)]! |= F_BUILD;
    return { u, v, ax, az, len, cx, cz };
  })();

  // Buildings: each tries its own sector, then wiggles until it fits.
  const pads: { plot: Plot; kind: string }[] = [];
  const boxFree = (x0: number, z0: number, x1: number, z1: number, margin: number) => {
    if (x0 - margin < 3 || z0 - margin < 3 || x1 + margin > S - 3 || z1 + margin > S - 3) return false;
    for (let z = z0 - margin; z < z1 + margin; z++) for (let x = x0 - margin; x < x1 + margin; x++) {
      if (flags[idx(x, z)]! & (F_PARK | F_BUILD | F_ZONE)) return false;
    }
    return true;
  };
  const place = (kind: string, W: number, D: number, a0: number, rMin: number, margin: number, maxCut: number): Plot | null => {
    for (let t = 0; t < 80; t++) {
      const a = a0 + (rand() - 0.5) * (0.3 + t * 0.03);
      for (let r = rMin; r < 66; r += 1) {
        const bx = C + Math.cos(a) * r, bz = C + Math.sin(a) * r;
        const rot = facing(bx, bz);
        const wx = rot % 2 ? D : W, wz = rot % 2 ? W : D;
        const x0 = Math.round(bx - wx / 2), z0 = Math.round(bz - wz / 2);
        // Nearest point of the footprint to the centre must clear the ring road.
        const nx = clamp(C, x0, x0 + wx - 1), nz = clamp(C, z0, z0 + wz - 1);
        if (Math.hypot(nx - C, nz - C) < RING_OUT + 3 + rand() * 3) continue;
        if (!boxFree(x0, z0, x0 + wx, z0 + wz, margin)) continue;
        // Don't dig a whole building into the mountainside.
        const y = H(bx, bz);
        let worst = 0;
        for (let z = z0; z < z0 + wz; z++) for (let x = x0; x < x0 + wx; x++) worst = Math.max(worst, h[idx(x, z)]! - y);
        if (worst > maxCut) break;
        const plot = new Plot(w, x0, z0, W, D, rot, y);
        for (let z = z0; z < z0 + wz; z++) for (let x = x0; x < x0 + wx; x++) flags[idx(x, z)]! |= F_BUILD;
        pads.push({ plot, kind });
        return plot;
      }
    }
    return null;
  };

  const stadium = place("stadium", 30, 24, sectorAngle("stadium"), 24, 3, 9);
  const library = place("library", 22, 15, sectorAngle("library"), 24, 3, 7);
  const dorms = place("dorms", 26, 9, sectorAngle("dorms"), 24, 3, 7);
  const garage = place("garage", 18, 14, sectorAngle("garage"), 24, 3, 7);
  const houses: Plot[] = [];
  for (let t = 0; t < 300 && houses.length < 4; t++) {
    const a = rand() * Math.PI * 2;
    const p = place("house", 7, 7, a, Math.round(foot(a) - 9 - rand() * 4), 4, 6);
    if (p) houses.push(p);
  }

  // Level each pad and blend the ground around it so you can walk up to the door.
  // Where skirts overlap, the nearest pad wins, so every door gets a clean approach.
  const natural = h.slice();
  const skirtD = new Float32Array(S * S).fill(Infinity);
  const flatten = (x0: number, z0: number, x1: number, z1: number, y: number, skirt: number) => {
    for (let z = z0 - skirt; z < z1 + skirt; z++) for (let x = x0 - skirt; x < x1 + skirt; x++) {
      if (!inMap(x, z)) continue;
      const i = idx(x, z);
      const d = Math.hypot(Math.max(x0 - x, 0, x - (x1 - 1)), Math.max(z0 - z, 0, z - (z1 - 1)));
      if (d === 0) {
        h[i] = y;
        skirtD[i] = 0;
        continue;
      }
      if (d > skirt || d >= skirtD[i]! || flags[i]! & (F_PARK | F_BUILD | F_ZONE)) continue;
      const k = d / skirt;
      h[i] = Math.round(y + (natural[i]! - y) * k * k);
      skirtD[i] = d;
    }
  };
  for (const { plot } of pads) {
    flatten(plot.x0, plot.z0, plot.x0 + plot.wx, plot.z0 + plot.wz, plot.y, 6);
    // A yard, so trees and poles don't crowd the doors.
    for (let z = plot.z0 - 3; z < plot.z0 + plot.wz + 3; z++) for (let x = plot.x0 - 3; x < plot.x0 + plot.wx + 3; x++) {
      if (inMap(x, z)) flags[idx(x, z)]! |= F_KEEP;
    }
  }

  // ---------- the stair path and cabin pad ----------

  const hA = H(lift.ax, lift.az);
  const hB = Math.max(hA + 6, Math.min(H(lift.cx, lift.cz), hA + Math.floor(lift.len * 0.85)));
  const rampAt = (t: number) => Math.round(hA + ((hB - hA) * clamp(t, 0, lift.len)) / lift.len);
  const cabinPad = { x0: lift.cx - 4, z0: lift.cz - 4, x1: lift.cx + 5, z1: lift.cz + 5 };
  flatten(cabinPad.x0, cabinPad.z0, cabinPad.x1, cabinPad.z1, hB, 4);
  const cabin = new Plot(w, lift.cx - 3, lift.cz - 3, 7, 7, facing(lift.cx, lift.cz), hB);
  {
    const { u, v, ax, az, len } = lift;
    for (let z = 0; z < S; z++) for (let x = 0; x < S; x++) {
      const t = (x - ax) * u[0] + (z - az) * u[1];
      const s = (x - ax) * v[0] + (z - az) * v[1];
      if (t < -0.5 || t > len + 2 || Math.abs(s) > 2.6) continue;
      const i = idx(x, z);
      if (flags[i]! & F_BUILD && t < len) continue;
      h[i] = rampAt(t);
      if (s < 1) top[i] = B.COBBLE;
    }
  }

  // ---------- river levels: each stretch no higher than the one upstream ----------

  {
    const { pts, rCliff, end } = river;
    const level: number[] = [];
    // The gorge is cut down to the valley floor where it meets the mountain foot;
    // the stream above the cliff runs well higher, and the drop is the waterfall.
    const poolLevel = Math.min(...pts.filter((p) => p.r < rCliff && p.rad > 2).map((p) => H(p.x, p.z) - 1));
    const fallTop = clamp(H(pts[0]!.x, pts[0]!.z) - 1, poolLevel + 7, poolLevel + 13);
    let run = poolLevel;
    for (const p of pts) {
      if (p.r >= rCliff) level.push(fallTop);
      else {
        run = Math.min(run, H(p.x, p.z) - 1);
        level.push(run);
      }
    }
    const stamp = (px: number, pz: number, rad: number, L: number, deep: number) => {
      disc(px, pz, rad, F_ZONE, (x, z, d) => {
        const i = idx(x, z);
        if (water[i] && water[i]! <= L) return;
        water[i] = L;
        h[i] = L - (d < rad - 1.2 ? deep : 1);
      });
    };
    pts.forEach((p, i) => stamp(p.x, p.z, p.rad, level[i]!, p.r >= rCliff ? 1 : 2));
    const pondL = level.at(-1)!;
    stamp(end.x, end.z, 5.5, pondL, 3);
    const foot = pts.find((p) => p.r < rCliff)!;
    mark("waterfall", foot.x, poolLevel + 1, foot.z);
    mark("pond", end.x, pondL + 1, end.z);
    // Banks never sit below the water next to them, or it'd float.
    pts.forEach((p, i) => {
      disc(p.x, p.z, p.rad + 2.2, 0, (x, z) => {
        const k = idx(x, z);
        if (!water[k] && h[k]! < level[i]!) h[k] = level[i]!;
      });
    });
    disc(end.x, end.z, 7.5, 0, (x, z) => {
      const k = idx(x, z);
      if (water[k]) return;
      if (h[k]! < pondL) h[k] = pondL;
      if (h[k]! <= pondL + 1) top[k] = B.SAND;
    });
  }

  // ---------- roads ----------

  for (let z = 0; z < S; z++) for (let x = 0; x < S; x++) {
    const r = Math.hypot(x - C, z - C);
    if (r >= RING_IN && r < RING_OUT) {
      top[idx(x, z)] = B.ASPHALT;
      flags[idx(x, z)]! |= F_ROAD;
    }
  }
  const roads: { x0: number; z0: number; x1: number; z1: number }[] = [];
  /** Straight 3-wide road from a door to the ring. Gives up rather than cross a building, the river or the stair path. */
  const road = (dx: number, dz: number) => {
    const d = Math.hypot(dx - C, dz - C);
    const ex = C + ((dx - C) / d) * (RING_IN + 1), ez = C + ((dz - C) / d) * (RING_IN + 1);
    const cells: number[] = [];
    const len = Math.hypot(ex - dx, ez - dz);
    for (let z = Math.floor(Math.min(dz, ez) - 2); z <= Math.ceil(Math.max(dz, ez) + 2); z++) {
      for (let x = Math.floor(Math.min(dx, ex) - 2); x <= Math.ceil(Math.max(dx, ex) + 2); x++) {
        if (!inMap(x, z)) continue;
        const t = clamp(((x - dx) * (ex - dx) + (z - dz) * (ez - dz)) / (len * len), 0, 1);
        if (Math.hypot(x - (dx + (ex - dx) * t), z - (dz + (ez - dz) * t)) > 1.5) continue;
        const f = flags[idx(x, z)]!;
        if (f & (F_BUILD | F_ZONE)) {
          // Right at the door it's fine to brush the building it's leading to.
          if (t * len < 2.5) continue;
          return false;
        }
        if (!(f & F_PARK)) cells.push(idx(x, z));
      }
    }
    for (const i of cells) {
      top[i] = B.ASPHALT;
      flags[i]! |= F_ROAD;
    }
    roads.push({ x0: dx, z0: dz, x1: ex, z1: ez });
    return true;
  };
  for (const { plot } of pads) road(...plot.door());
  road(lift.ax - lift.u[0] * 1.5, lift.az - lift.u[1] * 1.5);

  // ---------- voxels ----------

  const blocks = w.blocks;
  const at = (x: number, y: number, z: number) => (y * S + z) * S + x;
  for (let z = 0; z < S; z++) for (let x = 0; x < S; x++) {
    const i = idx(x, z);
    const y = h[i]!;
    let drop = 0;
    if (x > 0) drop = Math.max(drop, y - h[i - 1]!);
    if (x < S - 1) drop = Math.max(drop, y - h[i + 1]!);
    if (z > 0) drop = Math.max(drop, y - h[i - S]!);
    if (z < S - 1) drop = Math.max(drop, y - h[i + S]!);
    const rocky = drop >= 3 || (y > 38 && drop >= 2);
    blocks[at(x, 0, z)] = B.BEDROCK;
    for (let k = 1; k < y; k++) blocks[at(x, k, z)] = k > y - 4 && !rocky ? B.DIRT : B.STONE;
    const wl = water[i]!;
    blocks[at(x, y, z)] = top[i] || (wl ? (wl - y > 1 ? B.SAND : B.DIRT) : rocky ? B.STONE : B.GRASS);
    for (let k = y + 1; k <= wl; k++) blocks[at(x, k, z)] = B.WATER;
  }

  // The waterfall: wherever the top stream meets the pool, a sheet of water down the cliff.
  for (let z = 1; z < S - 1; z++) for (let x = 1; x < S - 1; x++) {
    const i = idx(x, z);
    const L = water[i]!;
    if (!L) continue;
    const up = Math.max(water[i - 1]!, water[i + 1]!, water[i - S]!, water[i + S]!);
    if (up >= L + 3) for (let y = L + 1; y <= up; y++) w.set(x, y, z, B.WATER);
  }

  // ---------- the park: cornucopia, pedestals, paths ----------

  for (let z = -22; z <= 22; z++) for (let x = -22; x <= 22; x++) {
    const r = Math.hypot(x, z);
    if (r > RING_OUT + 1) continue;
    let b: number = B.GRASS;
    if (r < 4.5) b = B.STONEBRICK;
    else if (r < 15.5 && (Math.abs(x) <= 1 || Math.abs(z) <= 1)) b = B.STONEBRICK;
    else if (r >= 10.5 && r < 12) b = B.COBBLE;
    else if (r >= 15.5 && r < 16.5) b = B.COBBLE;
    else if (r >= 16.5 && r < RING_IN) b = B.CONCRETE;
    else if (r >= RING_IN && r < RING_OUT) b = B.ASPHALT;
    else if (r >= RING_OUT) b = B.CONCRETE;
    w.set(C + x, PARK_Y, C + z, b);
  }
  chests.push({ x: C, y: PARK_Y + 1, z: C, good: true });
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    chests.push({ x: C + Math.round(Math.cos(a) * 3), y: PARK_Y + 1, z: C + Math.round(Math.sin(a) * 3), good: true });
  }
  const pedestals: MapData["pedestals"] = [];
  for (let i = 0; i < PEDESTAL_COUNT; i++) {
    const a = (i / PEDESTAL_COUNT) * Math.PI * 2;
    const px = C + Math.round(Math.cos(a) * 9);
    const pz = C + Math.round(Math.sin(a) * 9);
    w.set(px, PARK_Y, pz, B.GOLD);
    const yaw = Math.atan2(-(C - px), -(C - pz));
    pedestals.push({ x: px + 0.5, y: PARK_Y + 1, z: pz + 0.5, yaw });
  }

  // ---------- buildings ----------

  if (stadium) buildStadium(stadium, chests);
  if (library) buildLibrary(library, chests);
  if (dorms) buildDorms(dorms, chests);
  if (garage) buildGarage(garage, chests);
  houses.forEach((p, i) => buildHouse(p, chests, i, rand));
  buildCabin(cabin, chests);
  mark("park", C, PARK_Y + 1, C);
  for (const { plot, kind } of pads) {
    const name = kind === "house" ? `house${houses.indexOf(plot) + 1}` : kind;
    mark(name, ...([plot.center()[0], plot.y + 1, plot.center()[1]] as const));
  }
  mark("cabin", lift.cx, hB + 1, lift.cz);
  mark("chairlift", lift.ax, hA + 1, lift.az);

  // Chairlift posts and cable, one lane over from the stairs.
  {
    const { u, v, ax, az, len } = lift;
    const lane = (t: number): [number, number] => [Math.round(ax + u[0] * t + v[0] * 2), Math.round(az + u[1] * t + v[1] * 2)];
    const cableY = (t: number) => rampAt(t) + 8;
    const posts = new Set<number>();
    // The line stops short of the cabin pad so the top post never blocks the door.
    const end = len - 1.5;
    for (let t = 0; t <= end + 0.01; t += end / Math.max(2, Math.round(end / 7))) {
      const [x, z] = lane(t);
      posts.add(idx(x, z));
      const g = w.topSolid(x, z);
      for (let y = g + 1; y < cableY(t); y++) w.set(x, y, z, B.CONCRETE);
      w.set(x + Math.round(v[0]), cableY(t) - 1, z + Math.round(v[1]), B.WOOL_BLACK);
      w.set(x - Math.round(v[0]), cableY(t) - 1, z - Math.round(v[1]), B.WOOL_BLACK);
    }
    let lastChair = -9;
    for (let t = -1; t <= end; t += 0.25) {
      const [x, z] = lane(t);
      const y = cableY(t);
      w.set(x, y, z, B.WOOL_BLACK);
      if (t - lastChair >= 4 && !posts.has(idx(x, z)) && t > 1 && t < end - 1) {
        lastChair = t;
        w.set(x, y - 1, z, B.WOOL_BLACK);
        w.set(x, y - 2, z, B.WOOL_RED);
      }
    }
    // Base station: a little roofed platform where the path starts.
    const [bx, bz] = lane(-1);
    const g = h[idx(bx, bz)]!;
    for (let dz = -1; dz <= 1; dz++) for (let dx = -1; dx <= 1; dx++) {
      if (dx || dz) w.set(bx + dx, g, bz + dz, B.STONEBRICK);
      w.set(bx + dx, g + 5, bz + dz, B.STONEBRICK);
    }
    for (const [dx, dz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]] as const) for (let y = g + 1; y <= g + 4; y++) w.set(bx + dx, y, bz + dz, B.LOG);
  }

  // Telephone poles down one side of the longer roads.
  for (const r of roads) {
    const len = Math.hypot(r.x1 - r.x0, r.z1 - r.z0);
    const ux = (r.x1 - r.x0) / len, uz = (r.z1 - r.z0) / len;
    for (let t = 4; len > 12 && t < len - 3; t += 11) {
      const x = Math.round(r.x0 + ux * t - uz * 2.6), z = Math.round(r.z0 + uz * t + ux * 2.6);
      if (!inMap(x, z) || flags[idx(x, z)]! & (F_ROAD | F_BUILD | F_ZONE | F_PARK | F_KEEP)) continue;
      const g = h[idx(x, z)]!;
      for (let y = g + 1; y <= g + 6; y++) w.set(x, y, z, B.LOG);
      const along = Math.abs(ux) > Math.abs(uz);
      for (const s of [-1, 1]) w.set(x + (along ? 0 : s), g + 6, z + (along ? s : 0), B.PLANKS);
      disc(x, z, 1.5, F_KEEP);
    }
  }

  // ---------- trees, and the river's hidden chest ----------

  const trand = mulberry32(seed * 17 + 3);
  const tree = (x: number, z: number, g: number, trunk: number) => {
    for (let y = 1; y <= trunk; y++) w.set(x, g + y, z, B.LOG);
    for (let y = trunk - 2; y <= trunk + 1; y++) {
      const r = y >= trunk ? 1 : 2;
      for (let dz = -r; dz <= r; dz++) for (let dx = -r; dx <= r; dx++) {
        if (Math.abs(dx) === r && Math.abs(dz) === r && (y === trunk + 1 || trand() < 0.5)) continue;
        if (w.get(x + dx, g + y, z + dz) === B.AIR) w.set(x + dx, g + y, z + dz, B.LEAVES);
      }
    }
  };
  {
    const { pts } = river;
    const p = pts[Math.floor(pts.length * (0.55 + trand() * 0.15))]!;
    const x = Math.round(p.x), z = Math.round(p.z);
    const L = water[idx(x, z)]!;
    // Trunk standing in the stream, chest sunk in a hole in the bed beside it, under the leaves.
    tree(x, z, h[idx(x, z)]!, L - h[idx(x, z)]! + 5);
    const [sx, sz] = [[1, 0], [-1, 0], [0, 1], [0, -1]].map(([dx, dz]) => [x + dx!, z + dz!] as const).find(([cx, cz]) => water[idx(cx, cz)])!;
    const sl = water[idx(sx, sz)]!;
    w.set(sx, sl - 2, sz, B.SAND);
    w.set(sx, sl - 1, sz, B.WATER);
    chests.push({ x: sx, y: sl - 1, z: sz, good: true });
    mark("riverChest", sx, sl - 1, sz);
  }
  // Park trees between the pedestals.
  for (let i = 0; i < 6; i++) {
    const a = ((i + 0.25) / 6) * Math.PI * 2;
    tree(C + Math.round(Math.cos(a) * 13.5), C + Math.round(Math.sin(a) * 13.5), PARK_Y, 4 + (i % 2));
  }
  for (let tries = 0; tries < 2600; tries++) {
    const x = 3 + Math.floor(trand() * (S - 6)), z = 3 + Math.floor(trand() * (S - 6));
    const r = Math.hypot(x - C, z - C);
    const g = h[idx(x, z)]!;
    if (r < RING_OUT + 3 || w.get(x, g, z) !== B.GRASS || w.get(x, g + 1, z) !== B.AIR) continue;
    // Thick at the mountain foot, scattered on campus, thin up high.
    const m = r - foot(Math.atan2(z - C, x - C));
    const p = m < -12 ? 0.05 : m < 8 ? 0.35 : g < 40 ? 0.12 : 0;
    if (trand() > p) continue;
    let clear = true;
    for (let dz = -2; dz <= 2 && clear; dz++) for (let dx = -2; dx <= 2; dx++) {
      if (!inMap(x + dx, z + dz) || flags[idx(x + dx, z + dz)]! & (F_ROAD | F_BUILD | F_ZONE | F_KEEP | F_PARK)) {
        clear = false;
        break;
      }
    }
    if (!clear) continue;
    tree(x, z, g, 4 + Math.floor(trand() * 2));
    disc(x, z, 2.5, F_KEEP);
  }

  for (const c of chests) if (w.solid(c.x, c.y, c.z)) w.set(c.x, c.y, c.z, B.AIR);
  return { type: "valley", world: w, chests, pedestals, center: { x: C, y: PARK_Y + 1, z: C }, arenaRadius: ARENA_R, landmarks };
}

// ---------- landmarks ----------

/** Football stadium: a field with yard lines, bleachers both sides, chests in the tunnels under them. */
function buildStadium(p: Plot, chests: MapChest[]) {
  const L = p.W - 1; // 29
  for (let lz = 6; lz <= 17; lz++) for (let lx = 1; lx < L; lx++) {
    let b: number = B.GRASS;
    if (lx <= 3) b = B.WOOL_RED;
    else if (lx >= L - 3) b = B.WOOL_BLUE;
    else if (lz === 6 || lz === 17 || (lx - 4) % 5 === 0 || lx === 14 || lx === 15) b = B.WOOL_WHITE;
    p.set(lx, 0, lz, b);
  }
  // Goal posts at both ends.
  for (const lx of [0, L]) {
    p.fill(lx, 1, 11, lx, 3, 12, B.WOOL_YELLOW);
    p.fill(lx, 3, 9, lx, 3, 14, B.WOOL_YELLOW);
    p.fill(lx, 4, 9, lx, 6, 9, B.WOOL_YELLOW);
    p.fill(lx, 4, 14, lx, 6, 14, B.WOOL_YELLOW);
  }
  // Bleachers: five rows stepping up and away from the field, open underneath.
  for (const side of [0, 1]) {
    const row = (k: number) => (side ? 18 + k : 5 - k);
    const back = side ? 23 : 0;
    for (let k = 0; k < 5; k++) {
      const seat = side ? (k % 2 ? B.WOOL_WHITE : B.WOOL_RED) : k % 2 ? B.WOOL_WHITE : B.WOOL_BLUE;
      p.fill(1, 1 + k, row(k), L - 1, 1 + k, row(k), seat);
    }
    p.fill(0, 1, back, L, 6, back, B.CONCRETE);
    // Gates through the back wall into the tunnel.
    for (const gx of [7, 14, 21]) p.fill(gx, 1, back, gx + 1, 2, back, B.AIR);
    // Floodlights on the corners.
    for (const lx of [0, L]) {
      p.fill(lx, 7, back, lx, 12, back, B.CONCRETE);
      p.fill(lx === 0 ? 0 : L - 2, 13, back, lx === 0 ? 2 : L, 13, back, B.WOOL_WHITE);
    }
  }
  // Scoreboard over the far stand.
  p.fill(10, 7, 23, 19, 10, 23, B.WOOL_BLACK);
  p.fill(12, 8, 23, 13, 9, 23, B.WOOL_YELLOW);
  p.fill(16, 8, 23, 17, 9, 23, B.WOOL_YELLOW);
  p.chest(chests, 5, 1, 1);
  p.chest(chests, 18, 1, 1);
  p.chest(chests, 11, 1, 22);
  p.chest(chests, 24, 1, 22);
}

/** Library: brick hall with a glass roof, rows of bookshelves, chests in the aisles. */
function buildLibrary(p: Plot, chests: MapChest[]) {
  const W = p.W - 1, D = p.D - 1; // 21, 14
  p.fill(1, 0, 1, W - 1, 0, D - 1, B.PLANKS);
  for (let lz = 0; lz <= D; lz++) for (let lx = 0; lx <= W; lx++) {
    const edgeX = lx === 0 || lx === W, edgeZ = lz === 0 || lz === D;
    if (!edgeX && !edgeZ) continue;
    const pillar = (edgeX && edgeZ) || (lz === 0 && (lx === 5 || lx === W - 5));
    for (let ly = 1; ly <= 5; ly++) {
      const win = (ly === 2 || ly === 3) && (edgeZ ? lx % 3 === 2 && (lx < 8 || lx > 13) : lz % 3 === 2);
      p.set(lx, ly, lz, pillar ? B.STONEBRICK : win ? B.GLASS : B.BRICK);
    }
  }
  p.fill(10, 1, 0, 11, 3, 0, B.AIR);
  p.fill(0, 6, 0, W, 6, D, B.STONEBRICK);
  p.fill(6, 6, 4, W - 6, 6, D - 4, B.GLASS);
  for (let lx = 0; lx <= W; lx++) for (const lz of [0, D]) p.set(lx, 7, lz, B.STONEBRICK);
  for (let lz = 0; lz <= D; lz++) for (const lx of [0, W]) p.set(lx, 7, lz, B.STONEBRICK);
  p.fill(8, 8, 0, 13, 8, 0, B.STONEBRICK);
  p.fill(10, 9, 0, 11, 9, 0, B.STONEBRICK);
  // Circulation desks, then shelf rows either side of a centre aisle.
  p.fill(3, 1, 2, 6, 1, 2, B.PLANKS);
  p.fill(W - 6, 1, 2, W - 3, 1, 2, B.PLANKS);
  for (const lz of [4, 7, 10]) {
    p.fill(2, 1, lz, 8, 2, lz, B.BOOKSHELF);
    p.fill(W - 8, 1, lz, W - 2, 2, lz, B.BOOKSHELF);
  }
  p.fill(1, 1, D - 1, W - 1, 3, D - 1, B.BOOKSHELF);
  p.chest(chests, 3, 1, 5);
  p.chest(chests, W - 4, 1, 8);
  p.chest(chests, 7, 1, 11);
  p.chest(chests, W - 7, 1, 11);
}

/** A row of dorm rooms off a shared sidewalk, each with a bed and a chest. */
function buildDorms(p: Plot, chests: MapChest[]) {
  const rooms = 5;
  p.fill(0, 0, 0, rooms * 5, 0, 1, B.CONCRETE);
  p.fill(1, 0, 3, rooms * 5 - 1, 0, 7, B.PLANKS);
  for (let lx = 0; lx <= rooms * 5; lx++) for (let lz = 2; lz <= 8; lz++) {
    if (lx % 5 && lz !== 2 && lz !== 8) continue;
    for (let ly = 1; ly <= 3; ly++) p.set(lx, ly, lz, lx % 5 ? B.BRICK : B.STONEBRICK);
  }
  p.fill(0, 4, 1, rooms * 5, 4, 8, B.CONCRETE);
  for (let i = 0; i < rooms; i++) {
    const x = i * 5;
    p.fill(x + 2, 1, 2, x + 2, 2, 2, B.AIR);
    p.set(x + 4, 2, 2, B.GLASS);
    p.fill(x + 2, 2, 8, x + 3, 2, 8, B.GLASS);
    p.set(x + 1, 1, 6, B.WOOL_RED);
    p.set(x + 1, 1, 7, B.WOOL_WHITE);
    p.set(x + 4, 1, 3, B.PLANKS);
    p.chest(chests, x + 4, 1, 7);
  }
}

/** Two-deck parking garage with a ramp and block cars. */
function buildGarage(p: Plot, chests: MapChest[]) {
  const W = p.W - 1, D = p.D - 1; // 17, 13
  p.fill(0, 0, 0, W, 0, D, B.ASPHALT);
  for (const lx of [0, 6, 11, W]) for (const lz of [0, 7, D]) p.fill(lx, 1, lz, lx, 3, lz, B.CONCRETE);
  p.fill(0, 4, 0, W, 4, D, B.CONCRETE);
  for (let lx = 0; lx <= W; lx++) for (const lz of [0, D]) p.set(lx, 5, lz, B.CONCRETE);
  for (let lz = 0; lz <= D; lz++) for (const lx of [0, W]) p.set(lx, 5, lz, B.CONCRETE);
  // Ramp up the back, with a hole in the deck above it.
  for (let i = 1; i <= 4; i++) p.fill(14, 1, D - i, 15, i, D - i, B.CONCRETE);
  p.fill(14, 4, D - 3, 15, 4, D - 1, B.AIR);
  const car = (lx: number, ly: number, lz: number, color: number) => {
    p.fill(lx, ly, lz, lx + 3, ly, lz + 1, color);
    for (const [cx, cz] of [[0, 0], [3, 0], [0, 1], [3, 1]] as const) p.set(lx + cx, ly, lz + cz, B.WOOL_BLACK);
    p.fill(lx + 1, ly + 1, lz, lx + 2, ly + 1, lz + 1, B.GLASS);
  };
  car(1, 1, 1, B.WOOL_RED);
  car(7, 1, 1, B.WOOL_BLUE);
  car(1, 1, 11, B.WOOL_WHITE);
  car(7, 1, 10, B.WOOL_YELLOW);
  car(2, 5, 2, B.WOOL_BLUE);
  car(7, 5, 10, B.WOOL_RED);
  for (let lx = 1; lx <= 10; lx += 3) p.fill(lx, 0, 4, lx, 0, 5, B.WOOL_YELLOW);
  p.chest(chests, 12, 1, 4);
  p.chest(chests, 12, 5, 6);
}

/** Small house near the mountains: walls, windows, a hipped roof, a chest by the bed. */
function buildHouse(p: Plot, chests: MapChest[], n: number, rand: () => number) {
  const wall = [B.PLANKS, B.WOOL_WHITE, B.BRICK][n % 3]!;
  const roof = [B.BRICK, B.STONEBRICK, B.PLANKS, B.COBBLE][Math.floor(rand() * 4)]!;
  p.fill(1, 0, 1, 5, 0, 5, B.PLANKS);
  for (let lz = 0; lz <= 6; lz++) for (let lx = 0; lx <= 6; lx++) {
    const ex = lx === 0 || lx === 6, ez = lz === 0 || lz === 6;
    if (!ex && !ez) continue;
    for (let ly = 1; ly <= 3; ly++) p.set(lx, ly, lz, ex && ez ? B.LOG : wall);
  }
  for (const [lx, lz] of [[1, 0], [5, 0], [3, 6], [0, 3], [6, 3]] as const) p.set(lx, 2, lz, B.GLASS);
  p.fill(3, 1, 0, 3, 2, 0, B.AIR);
  for (let j = 0; j < 4; j++) p.fill(j, 4 + j, j, 6 - j, 4 + j, 6 - j, roof);
  p.set(1, 1, 5, B.WOOL_RED);
  p.set(1, 1, 4, B.WOOL_RED);
  p.chest(chests, 5, 1, 5);
}

/** Log cabin at the top of the chairlift, with the good chest. */
function buildCabin(p: Plot, chests: MapChest[]) {
  p.fill(1, 0, 1, 5, 0, 5, B.PLANKS);
  for (let lz = 0; lz <= 6; lz++) for (let lx = 0; lx <= 6; lx++) {
    if (lx % 6 && lz % 6) continue;
    for (let ly = 1; ly <= 3; ly++) p.set(lx, ly, lz, B.LOG);
  }
  for (const [lx, lz] of [[1, 0], [5, 0], [0, 3], [6, 3]] as const) p.set(lx, 2, lz, B.GLASS);
  p.fill(3, 1, 0, 3, 2, 0, B.AIR);
  for (let j = 0; j < 4; j++) p.fill(j, 4 + j, j, 6 - j, 4 + j, 6 - j, B.PLANKS);
  p.fill(5, 4, 5, 5, 8, 5, B.COBBLE);
  p.fill(1, 1, 5, 2, 1, 5, B.WOOL_RED);
  p.chest(chests, 5, 1, 5, true);
}
